import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import RAGCircle from './RAGCircle';
import InlineCoreSkills from './InlineCoreSkills';

function DashboardSubsection({
  node,
  defaultExpanded = false,
  renderLevel3Extras,
  onLeafToggle,
  childId,
  onSessionStarted,
  activeDashboardSession,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isLeaf = node.level === 3;
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const canExpand = isLeaf || hasChildren;

  const levelRowClass = {
    1: 'bg-[#F1EEF7]',
    2: 'bg-[#F8F5FF]',
    3: 'bg-[#FCFAFF]',
  };

  const levelAccentClass = {
    1: 'bg-gradient-to-b from-[#2F1E6B] to-[#5C3BD1]',
    2: 'bg-gradient-to-b from-[#5C3BD1] to-[#7B5BE2]',
    3: 'bg-gradient-to-b from-[#B5A3F0] to-[#D4C8F5]',
  };

  const handleToggle = () => {
    if (!canExpand) return;
    const next = !expanded;
    setExpanded(next);
    if (isLeaf) {
      onLeafToggle?.(node, next);
    }
  };

  const coreSkillOptions = node.coreSkillOptions || [];

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={handleToggle}
        className={`group w-full flex items-center gap-3 p-3 rounded-xl max-md:flex-wrap border border-gray-200/80 ${
          levelRowClass[node.level] ?? 'bg-white'
        } hover:border-indigo-200 hover:bg-white transition-all duration-200 ease-out hover:-translate-y-[1px] hover:shadow-[0_8px_20px_-14px_rgba(92,59,209,0.35)] motion-reduce:transform-none motion-reduce:transition-none`}
      >
        <div className="flex items-center gap-2 text-left min-w-0">
          <span
            className={`h-8 w-1.5 rounded-full shrink-0 ${levelAccentClass[node.level] ?? levelAccentClass[3]} transition-transform duration-200 group-hover:scale-y-105 motion-reduce:transform-none`}
            aria-hidden="true"
          />
          {canExpand ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-gray-600 shrink-0 transition-transform duration-200 group-hover:scale-105 motion-reduce:transform-none" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-600 shrink-0 transition-transform duration-200 group-hover:scale-105 motion-reduce:transform-none" />
            )
          ) : (
            <span className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          <span className="font-medium text-sm md:text-base truncate">{node.label}</span>
        </div>

        {isLeaf && coreSkillOptions.length > 0 && (
          <div className="ml-auto flex items-center shrink-0 max-md:order-last max-md:basis-full max-md:ml-0">
            <InlineCoreSkills
              options={coreSkillOptions}
              childId={childId}
              onSessionStarted={onSessionStarted}
              activeDashboardSession={activeDashboardSession}
            />
          </div>
        )}

        <div className="shrink-0 flex items-start gap-6 max-md:ml-auto max-md:gap-3">
          <RAGCircle
            statusBand={node.statusBand}
            statusLabel={node.statusLabel}
            averagePercent={node.averagePercent}
            heading="Overall"
          />
          <RAGCircle
            statusBand={node.latestStatusBand}
            statusLabel={node.latestStatusLabel}
            averagePercent={node.latestPercent}
            heading="Latest"
          />
        </div>
      </button>

      {expanded && !isLeaf && hasChildren ? (
        <div className="ml-4 mt-2 border-l pl-3">
          {node.children.map((child) => (
            <DashboardSubsection
              key={child.id}
              node={child}
              defaultExpanded={false}
              renderLevel3Extras={renderLevel3Extras}
              onLeafToggle={onLeafToggle}
              childId={childId}
              onSessionStarted={onSessionStarted}
              activeDashboardSession={activeDashboardSession}
            />
          ))}
        </div>
      ) : null}

      {isLeaf && expanded ? (
        <div className="ml-4 mt-2 border-l pl-3">{renderLevel3Extras?.(node, expanded)}</div>
      ) : null}
    </div>
  );
}

export default DashboardSubsection;
